/**
 * useAuth Hook
 * 
 * Custom hook to access authentication context.
 * Must be used within an AuthProvider.
 */

import { useContext } from 'react';
import { AuthContext, type AuthState } from '../contexts/AuthContext';

/**
 * Hook to access authentication state and methods
 * 
 * @example
 * const { isAuthenticated, user, signIn, signOut } = useAuth();
 * 
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

/**
 * Hook to check if user needs to complete their profile
 */
export function useNeedsProfileCompletion(): boolean {
  const { isAuthenticated, profile } = useAuth();
  
  // User is authenticated but hasn't completed profile
  return isAuthenticated && (!profile || !profile.profileCompleted);
}

/**
 * Hook to check if user is an admin
 */
export function useIsAdmin(): boolean {
  const { profile } = useAuth();
  return profile?.accountType === 'admin';
}

/**
 * Hook to check if user is a farm owner
 */
export function useIsFarmOwner(): boolean {
  const { profile } = useAuth();
  return profile?.accountType === 'owner' || profile?.accountType === 'admin';
}
